import React, { ReactNode } from 'react';

import { useAppliedDiscountOffer } from 'features/offer/hooks/use-applied-discount-offer';
import { StyleSheet, View } from 'react-native';

import Currency from 'components/currency';
import { Typography } from 'components/ui';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { CartItemOfferDiscount } from 'pages/cart/cart-item/cart-item-offer-discount';
import { CartItemOfferDiscountSkeleton } from 'pages/cart/cart-item/cart-item-offer-discount.skeleton';
import { useCartContext } from 'state/cart';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { getDesignToken } from 'styles/constants';

import { SkeletonStyled, TotalContainer } from './styles';

interface StickyFooterProps {
  priceTooLowCmp: ReactNode;
  CTAButton: ReactNode;
  isLoading: boolean;
  orderTotal: number;
}

export const CheckOutStickyFooter: React.FC<StickyFooterProps> = ({
  priceTooLowCmp,
  CTAButton,
  isLoading: isExternalLoading,
  orderTotal,
}) => {
  const { priceOrderFailure } = useCartContext();
  const { indexedMenu } = useUnifiedMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const isLoading = isExternalLoading || (isUnifiedMenuEnabled && !indexedMenu.isLoaded());

  const appliedLoyaltyDiscountOffer = useAppliedDiscountOffer();
  const totalAccessibilityLabel = `${t('total')}: ${formatCurrencyForLocale(orderTotal)}`;

  const totalContainer = priceOrderFailure ? null : (
    <TotalContainer accessible focusable accessibilityLabel={totalAccessibilityLabel}>
      <Typography focusable={false} fontFamily="heading" fontSize="xl" style={styles.totalLabel}>
        {t('total') + ': '}
      </Typography>
      <Currency
        focusable={false}
        testID="grand-total"
        amount={orderTotal}
        isBold
        textProps={{
          color: getDesignToken('text-default'),
          fontSize: 'xl',
          style: { margin: 0, flexGrow: 0 },
        }}
      />
    </TotalContainer>
  );

  return (
    <View testID="cart-payment" style={styles.buttonContainer}>
      {appliedLoyaltyDiscountOffer ? (
        isLoading ? (
          <CartItemOfferDiscountSkeleton selectedLoyaltyOffer={appliedLoyaltyDiscountOffer} />
        ) : (
          <CartItemOfferDiscount selectedLoyaltyOffer={appliedLoyaltyDiscountOffer} />
        )
      ) : null}
      {priceTooLowCmp}
      {isLoading ? <SkeletonStyled /> : totalContainer}
      {CTAButton}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: 'center',
    width: '100%',
  },
  totalLabel: {
    flexGrow: 0,
  },
});
